/**
 * ウィジェット iframe の「先読み」対応。
 *
 * embed.js は、ランチャーへのホバー / タッチ / フォーカスで iframe を先に読み込む
 * （`/widget/rag?...&prefetch=1`）。このとき、まだ開かれていないのに閲覧数
 * （trackPublicPageViewFn）が加算されないよう、「ユーザーが実際にウィジェットを開いた」
 * 合図（親ページからの `{ type: 'widget-opened' }`）が来るまで計測を保留する。
 *
 * - `prefetch=1` が無い場合（旧 embed.js / 直接アクセス / 公開ページ等）は従来どおり即時。
 * - メッセージ受信は import 時に登録する（Provider のマウント前に届いても取りこぼさない）。
 */
const isBrowser = typeof window !== 'undefined';

export const isWidgetPrefetch = (): boolean => {
  if (!isBrowser) return false;
  try {
    return new URLSearchParams(window.location.search).get('prefetch') === '1';
  } catch {
    return false;
  }
};

let opened = !isWidgetPrefetch();
const waiters: Array<() => void> = [];

if (isBrowser && !opened) {
  window.addEventListener('message', (event: MessageEvent) => {
    if (opened) return;
    // 親ページ（embed.js）からのメッセージのみ受け付ける
    if (event.source !== window.parent) return;
    if (!event.data || event.data.type !== 'widget-opened') return;
    opened = true;
    while (waiters.length) {
      const resolve = waiters.shift();
      try {
        resolve?.();
      } catch {
        /* noop */
      }
    }
  });
}

/** 同期で「開かれた（または先読みでない）」かを返す。 */
export const isWidgetOpened = (): boolean => opened;

/** ウィジェットが開かれた（または先読みでない）なら即 resolve。先読み中は開かれるまで待つ。 */
export const waitUntilWidgetOpened = (): Promise<void> => {
  if (opened) return Promise.resolve();
  return new Promise<void>((resolve) => {
    waiters.push(resolve);
  });
};
